import React, { useCallback, useEffect, useState } from "react";
import {
  ArrowLeft,
  ChatCircleDots,
  CheckCircle,
  Clock,
  FileText,
  PaperPlaneTilt,
  WarningCircle,
} from "@phosphor-icons/react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { platformSupport } from "@/api";

const STATUS_LABELS = {
  new: "Signalé",
  triage: "À qualifier",
  in_progress: "En cours",
  waiting_reporter: "À vérifier",
  planned: "Planifié",
  resolved: "Résolu",
  closed: "Fermé",
  rejected: "Non retenu",
  duplicate: "Doublon",
};

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("fr-FR");
}

function TicketStatus({ status }) {
  const Icon =
    status === "resolved" || status === "closed"
      ? CheckCircle
      : status === "waiting_reporter"
        ? WarningCircle
        : Clock;

  return (
    <span className="inline-flex items-center gap-2 text-sm">
      <Icon size={17} weight="duotone" />
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function SupportTickets() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [events, setEvents] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const ticketId = searchParams.get("ticket") || "";

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      setData(await platformSupport.listMyTickets({ page: 1, limit: 50 }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Impossible de charger vos signalements.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTicket = useCallback(async (id) => {
    if (!id) {
      setSelected(null);
      setEvents([]);
      return;
    }
    setDetailLoading(true);
    try {
      const result = await platformSupport.getMyTicket(id);
      setSelected(result.ticket);
      setEvents(result.events || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Signalement introuvable.");
      setSearchParams({});
    } finally {
      setDetailLoading(false);
    }
  }, [setSearchParams]);

  useEffect(() => { loadTickets(); }, [loadTickets]);
  useEffect(() => { loadTicket(ticketId); }, [ticketId, loadTicket]);

  const sendMessage = async () => {
    const value = message.trim();
    if (!selected || !value) return;
    setSaving(true);
    try {
      await platformSupport.addMessage(selected.id, value);
      setMessage("");
      await Promise.all([loadTicket(selected.id), loadTickets()]);
      toast.success("Message envoyé");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Message impossible à envoyer.");
    } finally {
      setSaving(false);
    }
  };

  const sendFeedback = async (resolved) => {
    if (!selected) return;
    setSaving(true);
    try {
      const result = await platformSupport.sendReporterFeedback(selected.id, {
        resolved,
        message: message.trim(),
      });
      setSelected(result.ticket);
      setEvents(result.events || []);
      setMessage("");
      await loadTickets();
      toast.success(resolved ? "Le problème est marqué comme résolu." : "Le support reprend le traitement.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Impossible d'enregistrer votre retour.");
    } finally {
      setSaving(false);
    }
  };

  if (ticketId) {
    return (
      <div className="mx-auto w-full max-w-5xl p-4 sm:p-6 lg:p-8" data-testid="support-ticket-detail">
        <button type="button" onClick={() => setSearchParams({})} className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-[var(--do-ink-soft)]">
          <ArrowLeft size={17} /> Mes signalements
        </button>

        {detailLoading && !selected ? (
          <div className="lirah-card p-6 text-sm text-[var(--do-muted)]">Chargement du signalement…</div>
        ) : selected ? (
          <div className="space-y-5">
            <section className="lirah-card p-5 sm:p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="label-eyebrow">{selected.reference}</div>
                  <h1 className="mt-1 font-heading text-2xl font-black text-[var(--do-ink)]">{selected.title}</h1>
                  <p className="mt-2 text-sm text-[var(--do-muted)]">Créé le {formatDate(selected.createdAt)}</p>
                </div>
                <div className="rounded-full border border-[var(--do-border)] px-3 py-1.5"><TicketStatus status={selected.status} /></div>
              </div>
              <div className="mt-5 rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4">
                <div className="label-eyebrow mb-2">Description</div>
                <p className="whitespace-pre-wrap text-sm text-[var(--do-ink)]">{selected.description}</p>
              </div>
              {(selected.attachments || []).map((attachment) => (
                <a key={attachment.downloadUrl} href={attachment.downloadUrl} target="_blank" rel="noreferrer" className="mt-4 block rounded-xl border border-[var(--do-border)] p-3 text-sm font-semibold text-[var(--do-accent)]">
                  Voir la capture — {attachment.name}
                </a>
              ))}
            </section>

            <section className="lirah-card p-5 sm:p-6">
              <div className="mb-4 flex items-center gap-2">
                <ChatCircleDots size={20} weight="duotone" />
                <h2 className="font-heading text-lg font-black text-[var(--do-ink)]">Échanges avec le support</h2>
              </div>
              <div className="space-y-3">
                {!events.length && <div className="rounded-xl border border-dashed border-[var(--do-border)] p-4 text-sm text-[var(--do-muted)]">Aucun message pour le moment.</div>}
                {events.map((event) => {
                  const fromSupport = event.payload?.authorKind === "support";
                  const text = event.payload?.message || (event.type === "reporter_feedback" ? (event.payload?.resolved ? "Le problème est résolu." : "Le problème persiste.") : "");
                  return (
                    <article key={event.id} className={`max-w-[86%] rounded-xl border border-[var(--do-border)] p-3 ${fromSupport ? "bg-[var(--do-accent-soft)]" : "ml-auto bg-[var(--do-card-bg)]"}`}>
                      <div className="text-xs font-bold text-[var(--do-ink)]">{fromSupport ? "Support Acta" : "Vous"}</div>
                      {text && <p className="mt-1 whitespace-pre-wrap text-sm text-[var(--do-ink)]">{text}</p>}
                      <div className="mt-1 text-[11px] text-[var(--do-muted)]">{formatDate(event.createdAt)}</div>
                    </article>
                  );
                })}
              </div>
              <div className="mt-5">
                <textarea value={message} onChange={(event) => setMessage(event.target.value)} rows={3} maxLength={5000} placeholder="Écrire une réponse au support…" className="w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm text-[var(--do-ink)]" data-testid="support-message-input" />
                {selected.status === "waiting_reporter" ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" disabled={saving} onClick={() => sendFeedback(true)} className="inline-flex items-center gap-2 rounded-[10px] bg-[var(--do-accent)] px-4 py-2 text-sm font-bold text-white disabled:opacity-50" data-testid="support-feedback-resolved"><CheckCircle size={18} />Le problème est résolu</button>
                    <button type="button" disabled={saving} onClick={() => sendFeedback(false)} className="inline-flex items-center gap-2 rounded-[10px] border border-[var(--do-border)] px-4 py-2 text-sm font-bold text-[var(--do-ink)] disabled:opacity-50" data-testid="support-feedback-persist"><WarningCircle size={18} />Le problème persiste</button>
                  </div>
                ) : (
                  <button type="button" disabled={saving || !message.trim()} onClick={sendMessage} className="mt-3 inline-flex items-center gap-2 rounded-[10px] bg-[var(--do-accent)] px-4 py-2 text-sm font-bold text-white disabled:opacity-50" data-testid="support-send-message"><PaperPlaneTilt size={18} />Envoyer</button>
                )}
              </div>
            </section>
          </div>
        ) : null}
      </div>
    );
  }

  const items = data?.items || [];
  return (
    <div className="mx-auto w-full max-w-6xl p-4 sm:p-6 lg:p-8" data-testid="support-tickets-page">
      <div className="mb-6">
        <div className="label-eyebrow">Support</div>
        <h1 className="mt-1 font-heading text-2xl font-black text-[var(--do-ink)]">Mes signalements</h1>
        <p className="mt-1 text-sm text-[var(--do-muted)]">Retrouvez vos demandes et les réponses du support Acta.</p>
      </div>
      <section className="lirah-card overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-[var(--do-muted)]">Chargement de vos signalements…</div>
        ) : !items.length ? (
          <div className="p-8 text-center"><FileText size={30} className="mx-auto text-[var(--do-muted)]" /><h2 className="mt-3 font-heading text-lg font-bold text-[var(--do-ink)]">Aucun signalement</h2><p className="mt-1 text-sm text-[var(--do-muted)]">Utilisez le bouton « Signaler » dans l'en-tête.</p></div>
        ) : (
          <div className="divide-y divide-[var(--do-border)]">
            {items.map((ticket) => (
              <button key={ticket.id} type="button" onClick={() => setSearchParams({ ticket: ticket.id })} className="grid w-full gap-2 px-4 py-4 text-left transition-colors hover:bg-[var(--do-surface-soft)] sm:grid-cols-[130px_1fr_150px_170px] sm:items-center" data-testid={`support-ticket-${ticket.id}`}>
                <span className="text-xs font-black text-[var(--do-accent)]">{ticket.reference}</span>
                <span className="font-semibold text-[var(--do-ink)]">{ticket.title}</span>
                <TicketStatus status={ticket.status} />
                <span className="text-xs text-[var(--do-muted)]">{formatDate(ticket.updatedAt)}</span>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
